import React from "react";
import styled from "styled-components";
import { Container, Section, Eyebrow } from "../components/ui/Container";
import { colors, fonts, media, radius } from "../theme";

const steps = [
  {
    n: "01",
    title: "Borrower applies",
    body: "The entrepreneur submits a 90 day mobile money statement and geo tagged photos of their stock from a mobile phone. No bank statements required.",
  },
  {
    n: "02",
    title: "Everpesa scores and lists",
    body: "Five layer verification covering identity, cashflow, stock evidence, local witness and repayment behaviour assigns a risk tier from A to D and posts the loan to the marketplace.",
  },
  {
    n: "03",
    title: "Investors fund the loan",
    body: "Multiple investors each fund a unit from UGX 10,000. Capital is disbursed within hours. Borrowers repay over 30 to 90 days and investors earn typical returns of 8 to 12%.",
  },
];

export const HowItWorks = () => (
  <Section style={{ background: colors.surfaceAlt }}>
    <Container>
      <Head>
        <div>
          <Eyebrow>How it works</Eyebrow>
          <h2>Three steps to a funded loan.</h2>
        </div>
        <p>
          A clean process from application to repayment, built around mobile and not paperwork.
        </p>
      </Head>

      <Grid>
        {steps.map((s, i) => (
          <Step key={s.n}>
            <Num>{s.n}</Num>
            <h3>{s.title}</h3>
            <p>{s.body}</p>
            {i < steps.length - 1 && <Connector />}
          </Step>
        ))}
      </Grid>

      <Result>
        <strong>The result:</strong> Borrowers get affordable capital. Investors earn competitive returns. Everpesa keeps the marketplace running.
      </Result>
    </Container>
  </Section>
);

const Head = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: end;
  gap: 32px;
  margin-bottom: 64px;
  h2 { margin-top: 18px; max-width: 14ch; font-family: ${fonts.display}; }
  p { font-size: 17px; max-width: 480px; justify-self: end; }
  ${media.md} {
    grid-template-columns: 1fr;
    gap: 18px;
    margin-bottom: 40px;
    p { justify-self: start; }
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  ${media.md} { grid-template-columns: 1fr; }
`;

const Step = styled.div`
  position: relative;
  background: white;
  border: 1px solid ${colors.line};
  border-radius: ${radius.lg};
  padding: 32px 28px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  transition: transform 240ms ease, box-shadow 240ms ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 16px 32px -12px rgba(10,15,31,0.12);
  }

  h3 { font-family: ${fonts.display}; }
  p { font-size: 15px; line-height: 1.65; }
`;

const Num = styled.span`
  font-family: ${fonts.display};
  font-size: 36px;
  font-weight: 800;
  background: linear-gradient(180deg, ${colors.brand}, ${colors.brandDark});
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  line-height: 1;
`;

const Connector = styled.span`
  position: absolute;
  top: 50px;
  right: -16px;
  width: 32px;
  height: 2px;
  background: linear-gradient(90deg, ${colors.brand}, transparent);
  ${media.md} { display: none; }
`;

const Result = styled.div`
  margin-top: 40px;
  background: ${colors.brand};
  color: white;
  padding: 22px 28px;
  border-radius: ${radius.md};
  font-size: 15.5px;
  text-align: center;
  strong { color: ${colors.gold}; }
`;
